import AnimatedSection from '../../../components/base/AnimatedSection';
import type { ReactNode } from 'react';

const rights: { icon: string; title: string; description: ReactNode; tag: string }[] = [
  {
    icon: 'ri-spotify-line',
    title: 'Unlimited Streaming',
    description:
      'Release on every streaming platform — Spotify, Apple Music, Tidal, Amazon Music, YouTube Music, and beyond. No limits on plays, streams, or royalties collected.',
    tag: 'All Platforms',
  },
  {
    icon: 'ri-store-line',
    title: 'Unlimited Sales',
    description:
      'Sell unlimited physical copies (CDs, vinyl, USB drives) and digital downloads. There is no revenue cap or per-unit fee. Every dollar you earn is yours.',
    tag: 'No Cap',
  },
  {
    icon: 'ri-film-line',
    title: 'Music Video Rights',
    description:
      'Use the instrumental in any video production — official music videos, lyric videos, documentaries, Instagram Reels, TikToks, and YouTube content.',
    tag: 'All Formats',
  },
  {
    icon: 'ri-mic-line',
    title: 'Live Performance',
    description:
      'Perform your song live at any venue, festival, club, or event worldwide. No per-show licensing fee. No restrictions on audience size or ticket pricing.',
    tag: 'Worldwide',
  },
  {
    icon: 'ri-global-line',
    title: 'Full Distribution',
    description:
      'Distribute freely via DistroKid, TuneCore, CD Baby, or any major label deal. Your exclusive license is fully compatible with every distributor and label contract.',
    tag: 'Label Ready',
  },
  {
    icon: 'ri-tv-2-line',
    title: 'Sync Licensing',
    description:
      'License your song to TV shows, films, commercials, and video games. We provide sync clearance documentation at no extra charge when needed.',
    tag: 'TV · Film · Ads',
  },
  {
    icon: 'ri-folder-music-line',
    title: 'WAV File + Full Stems',
    description: (
      <>
        Receive the final high-quality WAV file plus the complete stem pack — individual drum, melody, bass, and sample tracks. Need an MP3?{' '}
        <a href="sms:+15716995343" className="underline font-medium text-gray-700 whitespace-nowrap">
          Text +1 (571)-699-5343
        </a>{' '}
        and we&apos;ll deliver it as soon as possible.
      </>
    ),
    tag: 'Stems Included',
  },
  {
    icon: 'ri-lock-unlock-line',
    title: 'Beat Removed from Store',
    description:
      'Once purchased, your beat is permanently pulled from the marketplace. No other artist will ever own, license, or release music using this instrumental.',
    tag: 'Yours Forever',
  },
];

export default function RightsGrid() {
  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection animation="fade-up" className="text-center mb-16">
          <div className="inline-block px-4 py-2 border border-gray-300 rounded-full text-xs font-semibold text-gray-700 mb-4 tracking-widest uppercase">
            What You Get
          </div>
          <h2 className="text-6xl font-bold text-gray-900 mb-4 leading-tight">
            Every Right.<br />No Exceptions.
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            An exclusive license from NCMG is the most complete form of beat ownership in the industry. Here's exactly what transfers to you.
          </p>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {rights.map((right, i) => (
            <AnimatedSection
              key={right.title}
              animation="fade-up"
              delay={i * 80}
              className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-gray-400 transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-900 rounded-xl group-hover:bg-gray-800 transition-colors">
                  <i className={`${right.icon} text-xl text-white`}></i>
                </div>
                <span className="text-xs font-bold px-2 py-1 bg-amber-100 text-amber-700 rounded-full whitespace-nowrap">
                  {right.tag}
                </span>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-2">{right.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{right.description}</p>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
